import { useState, useEffect, useRef, useCallback } from "react";
import { Paper, TextField } from "@mui/material";
import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { observer } from "mobx-react";
import DeleteIcon from "@mui/icons-material/Delete";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import Highcharts from "highcharts";
import * as DraggablePoints from "highcharts/modules/draggable-points";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionActions";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ImageList from "@mui/material/ImageList";
import * as echarts from "echarts/core";
import {
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
} from "echarts/components";
import { BarChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";
import ReactEcharts from "echarts-for-react";
import { useSnackbar } from "notistack";
import { useProjectStore } from "../model/ProjectStore";
import { useViewState } from "../model/ViewState";
import CollapsiblePanel from "../components/CollapsiblePanel";

echarts.use([
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  BarChart,
  CanvasRenderer,
]);

// require("highcharts/modules/draggable-points")(Highcharts);
// DraggablePoints(Highcharts);
// DraggablePoints.default(Highcharts);

// ------------------ Server Interactions ------------------
export const ListCustomDistribution = (resultHandler) => {
  let getAPI = "/api/distribution";
  if (process.env.NODE_ENV !== "production") {
    getAPI = "http://localhost:7071" + getAPI;
  }
  fetch(getAPI, {
    method: "GET",
    headers: {
      Accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  }).then((response) => {
    if (!response.ok) {
      response.text().then((text) => {
        throw Error(response.statusText + ": " + text);
      });
    } else {
      response.json().then((parsedResponse) => resultHandler(parsedResponse));
    }
  });
};

export const GetCustomDistribution = (id, resultHandler) => {
  let getAPI = "/api/distribution/" + id;
  if (process.env.NODE_ENV !== "production") {
    getAPI = "http://localhost:7071" + getAPI;
  }
  fetch(getAPI, {
    method: "GET",
    headers: {
      Accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  }).then((response) => {
    if (!response.ok) {
      response.text().then((text) => {
        throw Error(response.statusText + ": " + text);
      });
    } else {
      response.json().then((parsedResponse) => resultHandler(parsedResponse));
    }
  });
};

const saveToCloud = (distribution, saved) => {

  for (let i = 0; i < distribution.distribution.length; i++) {
    let progress = distribution.distribution[i];
    if (progress.y === undefined) {
      distribution.distribution[i] = progress;
    } else {
      parseInt(progress.y);
    }
  }
  const data = convertToString(distribution.distribution);
  let saveAPI = "/api/distribution";
  if (process.env.NODE_ENV !== "production") {
    saveAPI = "http://localhost:7071" + saveAPI;
  }
  fetch(saveAPI, {
    method: "Post",
    headers: {
      Accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      Id: distribution.id,
      Name: distribution.name,
      Distribution: data,
    }),
  }).then((response) => {
    if (!response.ok) {
      response.text().then((text) => {
        throw Error(response.statusText + ": " + text);
      });
    } else {
      response.json();
      saved(true);
    }
  });
};

const deleteFromCloud = (distributions, id) => {
  let deleteAPI = "/api/distribution/delete/" + id;
  if (process.env.NODE_ENV !== "production") {
    deleteAPI = "http://localhost:7071" + deleteAPI;
  }
  fetch(deleteAPI, {
    method: "POST",
    headers: {
      Accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  }).then((response) => {
    if (!response.ok) {
      response.text().then((text) => {
        throw Error(response.statusText + ": " + text);
      });
    } else {
      response.json();
    }
  });
};

const createFromCloud = (
  distribution,
  setSelectedDistribution,
  distributions,
  view
) => {
  let getAPI = "/api/distribution/create";
  if (process.env.NODE_ENV !== "production") {
    getAPI = "http://localhost:7071" + getAPI;
  }

  const data = convertToString(distribution.distribution);
  fetch(getAPI, {
    method: "post",
    headers: {
      Accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      Id: distribution.id,
      Name: distribution.name,
      Distribution: data,
    }),
  }).then((response) => {
    if (!response.ok) {
      response.text().then((text) => {
        throw Error(response.statusText + ": " + text);
      });
    } else {
      response.json().then((result) => {
        result.selected.distribution = convertToArray(
          result.selected.distribution
        );
        setSelectedDistribution(result.selected);
        distributions.unshift(result.selected);
        view.selectedDistributionId = result.selected.id;
      });
    }
  });
};

// ------------------ Visual functions ------------------
const DistributionChart = ({
  selectedDistribution,
  setSelectedDistribution,
  view,
  distributions,
}) => {
  const data = [];
  for (let i = 0; i < selectedDistribution.distribution.length; i++) {
    data.push([i + 1, selectedDistribution.distribution[i]]);
  }

  const echartRef = useRef(null);
  const echartInstance = echartRef.current?.getEchartsInstance();

  const display = {
    name: selectedDistribution.name,
    data: data,
    type: "bar",
  };

  const symbolSize = 10;

  const options = {
    title: {
      left: "center",
      text: selectedDistribution.name,
    },
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
    },
    grid: {
      containLabel: true,
      show: true,
    },
    xAxis: {
      name: "Period",
      max: selectedDistribution.distribution.length + 1,
      type: "value",
    },
    yAxis: {
      type: "value",
    },
    series: display,
  };

  if (echartRef && echartRef.current) {
    setTimeout(() => {
      echartInstance.setOption({
        graphic: data.map((item, dataIndex) => {
          return {
            type: "circle",
            position: echartInstance.convertToPixel("grid", item),
            shape: {
              cx: 0,
              cy: 0,
              r: symbolSize / 4,
            },
            //invisible: true,
            draggable: "vertical",
            ondrag: function (dx, dy) {
              onPointDragging(dataIndex, [this.x, this.y]);
            },
            z: 100,
          };
        }),
      });
    }, 450);

    const onPointDragging = (dataIndex, pos) => {
      selectedDistribution = getDistribution(
        distributions,
        view.selectedDistributionId
      );
      data[dataIndex] = echartInstance.convertFromPixel("grid", pos);

      const newDist = { ...selectedDistribution };
      const coordinates = echartInstance.convertFromPixel("grid", pos);
      newDist.distribution[dataIndex] = coordinates[1];
      setSelectedDistribution(newDist);
      view.selecteDistributionId = newDist.id;

      echartInstance.setOption({
        series: [
          {
            data: data,
          },
        ],
      });
      echartInstance.setOption({
        graphic: data.map((item, dataIndex) => {
          return {
            type: "circle",
            position: echartInstance.convertToPixel("grid", item),
            shape: {
              cx: 0,
              cy: 0,
              r: symbolSize / 4,
            },
            z: 100,
          };
        }),
      });
    };
  }

  return <ReactEcharts option={options} ref={echartRef} />;
};

const GenerateList = ({
  distributionSelect,
  distributions,
  deleteDistribution,
}) => {
  return distributions.map((distribution) => (
    <ListItem
      button
      key={distribution.id + "_distributionList"}
      onClick={() => distributionSelect(distribution.id)}
    >
      <ListItemText primary={distribution.name} />
      <ListItemSecondaryAction>
        <IconButton
          edge="end"
          aria-label="delete"
          onClick={() => deleteDistribution(distribution.id)}
        >
          <DeleteIcon />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  ));
};

const DistributionField = ({ selectedDistribution, view, myChangeHandler }) => {
  if (selectedDistribution.length !== 0) {
    return selectedDistribution.distribution.map((progress, index) => (
      <TextField
        key={view.selectedDistributionId + index + "distributionField"}
        autoComplete="off"
        error={progress.y === undefined ? isNaN(progress) : false}
        label={"Period " + (index + 1)}
        id={view.selectedDistributionId + index + "distributionField"}
        value={progress.y === undefined ? progress : parseFloat(progress.y)}
        variant="filled"
        size="medium"
        name={index.toString()}
        onChange={myChangeHandler}
      />
    ));
  } else {
    return <div></div>;
  }
};

// ------------------ Helper Functions ------------------
function getDistribution(distributions, id) {
  let distribution = distributions.filter((x) => x.id === id);
  if (distribution.length !== 0) {
    return distribution[0];
  } else {
    return { id: "", name: "", distribution: [] };
  }
}

function convertToString(data) {
  const dataString = data.map(String);
  return dataString.join(";");
}

function convertToArray(data) {
  if (data !== undefined) {
    const dataArray = data.split(";");
    return dataArray.map((x) => parseFloat(x, 10));
  }
}

const createDistribution = (
  distributionName,
  setSelectedDistribution,
  setNewDistribution,
  distributions,
  view
) => {
  setNewDistribution("");
  if (distributionName !== "") {
    let distribution = { id: "", name: distributionName, distribution: [] };
    createFromCloud(distribution, setSelectedDistribution, distributions, view);
  }
};

const ConfigureDistributionView = () => {
  const { enqueueSnackbar } = useSnackbar();
  const project = useProjectStore();
  const view = useViewState();
  let [treeDrawerOpen, setTreeDrawerOpen] = useState(true);
  let [open, setOpen] = useState(false);
  let [distributions, setDistributions] = useState([]);
  let [newDistribution, setNewDistribution] = useState("");
  const [drawerWidth, setDrawerWidth] = useState(240);
  const [savingStarted, setSavingStarted] = useState(false);
  const [savingCompleted, setSavingCompleted] = useState(false);
  let [selectedDistribution, setSelectedDistribution] = useState({
    id: "",
    name: "",
    distribution: [],
  });

  useEffect(() => {
    project.checkProjUpdate(project.id, project.currentHash, enqueueSnackbar)
      .catch((error) => {
        enqueueSnackbar(error.message, { variant: "error" });
        if (error.cause === "opaqueredirect") {
          setTimeout(() => {
            window.location.href = "/login";
          }, 1000);
        }
      });

    ListCustomDistribution((distributions) => {
      const result = distributions.map((distribution) => {
        distribution.distribution = convertToArray(distribution.distribution);
        return {
          id: distribution.id,
          name: distribution.name,
          distribution: distribution.distribution,
        };
      });
      setDistributions(result);
    });
  }, [enqueueSnackbar, project]);

  const myChangeHandler = (event, view, selectedDistribution) => {
    let nam = event.target.name;
    let val = event.target.value;
    const newDist = { ...selectedDistribution };
    const index = parseInt([nam], 10);

    if (val !== "") {
      val = parseInt(val, 10);
    } else {
      val = 0;
    }

    newDist.distribution[index] = val;
    setSelectedDistribution(newDist);
  };

  const selectDistribution = (distributionId) => {
    GetCustomDistribution(distributionId, (result) => {
      const dist = {
        id: result.id,
        name: result.name,
        distribution: convertToArray(result.distribution),
      };
      setSelectedDistribution(dist);
      view.selectedDistributionId = distributionId;
    });
  };

  const savingDistribution = () => {
    setSavingStarted(true);
    saveToCloud(selectedDistribution, setSavingCompleted);
  };

  const deleteDistribution = (id) => {
    if (id !== selectedDistribution.id) {
      const newList = distributions.filter((x) => x.id !== id);
      deleteFromCloud(distributions, id);
      setDistributions(newList);
    } else {
      setOpen(true);
    }
  };

  const handleClose = (event, reason) => {
    setOpen(false);
  };

  const handleMouseDown = (e) => {
    document.addEventListener("mouseup", handleMouseUp, true);
    document.addEventListener("mousemove", handleMouseMove, true);
  };

  const handleMouseUp = () => {
    document.removeEventListener("mouseup", handleMouseUp, true);
    document.removeEventListener("mousemove", handleMouseMove, true);
  };

  const handleMouseMove = useCallback((e) => {
    const newWidth = e.clientX - document.getElementById("configureDistributionViewResizableDrawer").offsetLeft;
    if (newWidth > 100 && newWidth < 1000) {
      setDrawerWidth(newWidth);
    }
  }, []);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSavingStarted(false);
    setSavingCompleted(false);
  };

  return (
    <div>
      <Snackbar
        title="Saving Started"
        open={savingStarted}
        autoHideDuration={1000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="info"
          sx={{ width: "100%" }}
        >
          Saving the updated custom distribution...
        </Alert>
      </Snackbar>
      <Snackbar
        title="Saving Successul"
        open={savingCompleted}
        autoHideDuration={1000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          Successfully saved the updated custom distribution!
        </Alert>
      </Snackbar>
      <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
        <Alert
          onClose={handleClose}
          severity="error"
          elevation={6}
          variant="filled"
        >
          Cannot delete distribution you are currently on
        </Alert>
      </Snackbar>
      <div>
        <div>
          <Typography variant="h4" gutterBottom component="h2">
            Configure Distribution
          </Typography>
        </div>
        <div style={{ display: "flex", padding: "5px", minHeight: "800px", overflowX: "auto" }}>
          <CollapsiblePanel
            width={drawerWidth}
            setWidth={setDrawerWidth}
            open={treeDrawerOpen}
            onToggle={() => setTreeDrawerOpen(!treeDrawerOpen)}
            menuActions={
              <>
                <TextField
                  variant="standard"
                  key={view.selectedDistributionId + "NewDistributionField"}
                  label={"New Distribution"}
                  fullWidth={true}
                  onChange={(event) => setNewDistribution(event.target.value)}
                />
                <IconButton
                  onClick={() =>
                    createDistribution(
                      newDistribution,
                      setSelectedDistribution,
                      setNewDistribution,
                      distributions,
                      view
                    )
                  }
                  color="inherit"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </>
            }
          >
            <List>
              <GenerateList
                distributionSelect={(distributionId) =>
                  selectDistribution(distributionId)
                }
                distributions={distributions}
                deleteDistribution={deleteDistribution}
              />
            </List>
          </CollapsiblePanel>
          <div
            style={{ display: "flex", flexDirection: "row", margin: "0px 8px" }}
          >
            <Paper
              style={{ minWidth: "800px", minHeight: "400px", padding: "8px" }}
            >
              <Typography color="textPrimary" variant="h6">
                Distribution
              </Typography>

              <IconButton onClick={() => savingDistribution()} color="inherit">
                <SaveIcon />
              </IconButton>
              <div>
                <DistributionChart
                  selectedDistribution={selectedDistribution}
                  setSelectedDistribution={setSelectedDistribution}
                  view={view}
                  distributions={distributions}
                />
              </div>
              <div>
                <Accordion style={{ width: "1000px" }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <Typography>
                      Manually enter values
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <ImageList cols={5}>
                      <DistributionField
                        selectedDistribution={selectedDistribution}
                        view={view}
                        myChangeHandler={(event) =>
                          myChangeHandler(event, view, selectedDistribution)
                        }
                      />
                    </ImageList>
                  </AccordionDetails>
                </Accordion>
              </div>
            </Paper>
          </div>
        </div>
      </div>
    </div>
  );
};


export default observer(ConfigureDistributionView);
